import { useEffect } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'

import { checkAuthentictionOrRedirectToLogin, logger } from '$utils'
import useStockStore from './routes/stock/store'
import useCompanyStore from './routes/settings/company/store'

// This variable is independent of the React lifecycle and is used to force a
// re-mount of the React app when the AngularJS app tells us that the page
// content has changed, but the url has stayed the same.
let remountCounter = 0

/**
 * The AppRoot component is (currently) an invisible React component that is
 * permanently running and listening for any navigation events that have been
 * emitted by the legacy AngularJS app. It responds to these by triggering a
 * navigation action in the React app router - the end result being that any
 * pages that are powered by React end up being rendered.
 * @returns
 */
const AppRoot = () => {
  const navigate = useNavigate()

  useEffect(() => {
    const handleAngularNav = (ev: Event) => {
      const { to: destinationUrl, updatedPageContent } = (ev as CustomEvent)
        .detail
      logger.info(
        'Processing AngularJS navigation to %s %s',
        destinationUrl,
        updatedPageContent
          ? 'and we need to remount due to updated page conent'
          : ''
      )

      // TODO - remove this once the AngularJS app is no longer in use.
      //
      // We check the auth state on any AngularJS navigation to maintain backward
      // compatibility with what the AngularJS app did itself when it was
      // responsible for checking the users auth status. As we now 'own' this in
      // React, it's moved here to keep things in once place.
      checkAuthentictionOrRedirectToLogin()

      navigate(destinationUrl, { replace: true })
      if (updatedPageContent) {
        // When AngularJS has told us that the page content has been updated we
        // force a remount on the React app. This is expected to occur when the
        // url hasn't changed but the content is expected to - e.g. when a user
        // has updated the currently active company in the AngularJS app.
        remountCounter++
      }
    }

    // When the app context changes we need to force a remount of the React app
    // so that any company specific data is updated.
    const handleAppContextChange = (ev: Event) => {
      const { type } = (ev as CustomEvent).detail || {}
      // Only reset stock data when switching companies
      if (type === 'company') {
        useStockStore.getState().resetSharedStockSlice()
        useCompanyStore.getState().resetCompanyPreferencesSlice()
      }
    }

    window.addEventListener('@@loaded/angularNavigation', handleAngularNav)
    window.addEventListener('@@loaded/appContextChange', handleAppContextChange)

    // Cleanup function removes listeners when effect re-runs or component unmounts
    return () => {
      window.removeEventListener('@@loaded/angularNavigation', handleAngularNav)
      window.removeEventListener(
        '@@loaded/appContextChange',
        handleAppContextChange
      )
    }
  }, [navigate])

  // The Outlet component is a special component that is used to render
  // any child routes that are defined in the router definition array -
  // in this case that's the pages we actually want to show injected into
  // the AngularJS parent app.
  return <Outlet key={remountCounter} />
}

export default AppRoot
